import { Component, Vue, Watch } from "vue-property-decorator";
import { Result, Button, Modal } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import ConversationRenderer from "@/components/conversation-renderer/conversation-renderer.vue";
import { fmtMsg, GLUtil } from "commonui";
import { Paths } from "@/util/paths";
import { AccessLevel, BuilderPageType } from "@/util/enum";
import EventBus from "@/commonui/utils/eventBus";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { deepClone, disabledDueToAccessLevel, getRouteAccessLevel, showErrorMessage } from "@/util/func";
import { ConversationBuilderService } from "@/services/conversation-builder";
import { generateDefaultConversation } from "@/views/conversation-builder/conversation-builder-util";
import { IRendererActions } from "@/components/conversation-renderer/conversation-renderer-util";
import { CommonLocale, ConversationBuilderLocale } from "@/locales/localeid";

@Component({
	components: { Result, ConversationRenderer, Button }
})
export default class Conversations extends Vue {
	public conversationStore = getModule(ConversationBuilderState);
	public titleListStore = getModule(TitleListState);
	public deleting = false;
	public lastNodes = {};

	created() {
		this.actions.forEach((a) => EventBus.$on(a.eventName, this[a.eventName]));
		this.lastNodes = this.conversationStore.nodes ? deepClone(this.conversationStore.nodes) : {};
	}

	beforeDestroy() {
		this.actions.forEach((a) => EventBus.$off(a.eventName, this[a.eventName]));
	}

	get nodes() {
		const { nodes } = this.conversationStore;
		if (!nodes) {
			return null;
		}
		return Object.keys(nodes).length || Object.keys(this.lastNodes).length;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	get actions(): IRendererActions[] {
		return this.disabledDueToAccessLevel
			? []
			: [
					{ icon: "create", eventName: "goToBuilder" },
					{
						icon: "delete",
						eventName: "deleteConversation",
						customClasses: ["cb-action--delete"]
					}
			  ];
	}

	@Watch("titleListStore.editeContentSelectedChapterId")
	updateNodes() {
		if (this.titleListStore.editeContentSelectedChapterId !== "none") {
			this.conversationStore.fetchContentConversation(this.titleListStore.editeContentSelectedChapterId);
		}
	}

	public goToBuilder() {
		const { editeContentSelectedChapterId } = this.titleListStore;
		const contentId =
			editeContentSelectedChapterId !== "none" ? editeContentSelectedChapterId : this.$route.params.id;

		this.$router.push(
			GLUtil.pathStringify(Paths.ContentConversationBuilder, {
				id: contentId,
				pagetype: BuilderPageType.ContentInfo
			})
		);
	}

	public async deleteConversation() {
		const onOk = async () => {
			this.deleting = true;
			const { id } = this.conversationStore.conversationProperties;
			try {
				await ConversationBuilderService.deleteConversation({
					conversationId: id as number,
					contentId: this.$route.params.id
				});

				this.conversationStore.setConversationProps(generateDefaultConversation());
				this.conversationStore.setMetaData({ changes: [], nodes: {} });
				this.lastNodes = {};
				this.deleting = false;
			} catch ({ error_code, error_description }) {
				showErrorMessage(
					{ error_code, error_description },
					fmtMsg(CommonLocale.CommonContentConversationAssociationDeleteMessage)
				);
				this.deleting = false;
			}
		};

		Modal.confirm({
			onOk,
			okText: fmtMsg(ConversationBuilderLocale.ConversationDeleteContent),
			cancelButtonProps: { props: { disabled: this.deleting } },
			title: fmtMsg(ConversationBuilderLocale.ConversationDeleteWarn)
		});
	}
}
